import { create } from 'zustand';
import type { Difficulty, PublicPuzzle, SerializedBoardDto, TileDto } from '../api/puzzles';

export interface StagedPlacement {
  row: number;
  col: number;
  tile_id: string;
  /** Letter showing on the board — for a blank this is the designated letter. */
  letter: string;
  is_blank: boolean;
}

export type LifeStatus = 'IN_PROGRESS' | 'WON' | 'LOST';

export interface MovePlayed {
  word: string;
  score: number;
  /** The tiles played, kept so a guest run can be replayed server-side. */
  placements: StagedPlacement[];
}

export interface SealedLife {
  life_number: number;
  moves: MovePlayed[];
  status: 'WON' | 'LOST';
  final_score: number;
}

export interface GameSnapshot {
  lifeId: string | null;
  puzzle: PublicPuzzle | null;
  difficulty: Difficulty | null;
  /** Committed tiles (server-authoritative, or guest-local committed moves). */
  board: SerializedBoardDto;
  /** Full current rack including tiles that are currently staged. */
  rack: TileDto[];
  /** Undrawn bag tiles in draw order (guest mode refills locally from here). */
  bag: TileDto[];
  staged: StagedPlacement[];
  selectedTileId: string | null;
  cumulativeScore: number;
  movesUsed: number;
  /** Primary word + score of each committed move this life (result screen + share). */
  moveHistory: MovePlayed[];
  /** Lives already finished today, sealed in order — the claimable record. */
  previousLives: SealedLife[];
  lifeNumber: number;
  status: LifeStatus | null;
  /** True for guest/practice play — moves commit locally, nothing persists. */
  localMode: boolean;
  /** Whether any life today was won (drives the end-of-day modal). */
  dayWon: boolean;
}

export interface GameActions {
  hydrate: (snapshot: Partial<GameSnapshot>) => void;
  addPlacement: (row: number, col: number, tile: TileDto, letter?: string) => void;
  removePlacement: (row: number, col: number) => void;
  /** Move a staged tile to another cell; swaps if the target holds another staged tile. */
  moveStaged: (fromRow: number, fromCol: number, toRow: number, toCol: number) => void;
  recallAll: () => void;
  undoLast: () => void;
  selectTile: (tile_id: string | null) => void;
  designateBlank: (row: number, col: number, letter: string) => void;
  /** Re-arrange the rack: move a tile to a display position (index within the visible rack). */
  reorderRack: (tile_id: string, toDisplayIndex: number) => void;
  /** Guest mode: commit the staged tiles as a move scored by the local engine. */
  applyLocalMove: (score: number, word: string) => void;
  /** Guest mode: give up the current life. */
  finishLocalLife: () => void;
  /** Guest mode: start the next life from the identical puzzle setup. */
  startNextLocalLife: () => void;
  reset: () => void;
}

const INITIAL: GameSnapshot = {
  lifeId: null,
  puzzle: null,
  difficulty: null,
  board: { placed: [] },
  rack: [],
  bag: [],
  staged: [],
  selectedTileId: null,
  cumulativeScore: 0,
  movesUsed: 0,
  moveHistory: [],
  previousLives: [],
  lifeNumber: 1,
  status: null,
  localMode: true,
  dayWon: false,
};

export const useGameStore = create<GameSnapshot & GameActions>((set, get) => ({
  ...INITIAL,

  hydrate: (snapshot) => set({ ...snapshot, staged: [], selectedTileId: null }),

  addPlacement: (row, col, tile, letter) => {
    const { staged, board, rack } = get();
    const occupied =
      staged.some((p) => p.row === row && p.col === col) ||
      board.placed.some((p) => p.row === row && p.col === col);
    const inRack = rack.some((t) => t.tile_id === tile.tile_id);
    const alreadyStaged = staged.some((p) => p.tile_id === tile.tile_id);
    if (occupied || !inRack || alreadyStaged) return;
    set({
      staged: [
        ...staged,
        {
          row,
          col,
          tile_id: tile.tile_id,
          letter: tile.is_blank ? (letter ?? '') : tile.letter,
          is_blank: tile.is_blank,
        },
      ],
      selectedTileId: null,
    });
  },

  removePlacement: (row, col) =>
    set((s) => ({ staged: s.staged.filter((p) => !(p.row === row && p.col === col)) })),

  moveStaged: (fromRow, fromCol, toRow, toCol) =>
    set((s) => {
      if (fromRow === toRow && fromCol === toCol) return s;
      const fromIdx = s.staged.findIndex((p) => p.row === fromRow && p.col === fromCol);
      if (fromIdx === -1) return s;
      // Never displace a committed tile.
      if (s.board.placed.some((p) => p.row === toRow && p.col === toCol)) return s;
      const toIdx = s.staged.findIndex((p) => p.row === toRow && p.col === toCol);
      const staged = s.staged.map((p) => ({ ...p }));
      staged[fromIdx] = { ...staged[fromIdx]!, row: toRow, col: toCol };
      if (toIdx !== -1) {
        // Target holds another staged tile — it takes the source cell (swap).
        staged[toIdx] = { ...staged[toIdx]!, row: fromRow, col: fromCol };
      }
      return { staged, selectedTileId: null };
    }),

  recallAll: () => set({ staged: [], selectedTileId: null }),

  undoLast: () => set((s) => ({ staged: s.staged.slice(0, -1) })),

  selectTile: (tile_id) => set({ selectedTileId: tile_id }),

  designateBlank: (row, col, letter) =>
    set((s) => ({
      staged: s.staged.map((p) =>
        p.row === row && p.col === col && p.is_blank ? { ...p, letter } : p,
      ),
    })),

  reorderRack: (tile_id, toDisplayIndex) =>
    set((s) => {
      const moving = s.rack.find((t) => t.tile_id === tile_id);
      if (!moving) return s;
      // The visible rack hides staged tiles, so translate the display slot
      // into "insert before this tile" within the full rack array.
      const visibleOthers = availableRack(s).filter((t) => t.tile_id !== tile_id);
      const before = visibleOthers[toDisplayIndex];
      const rack = s.rack.filter((t) => t.tile_id !== tile_id);
      const insertAt = before ? rack.findIndex((t) => t.tile_id === before.tile_id) : rack.length;
      rack.splice(insertAt < 0 ? rack.length : insertAt, 0, moving);
      return { rack };
    }),

  applyLocalMove: (score, word) =>
    set((s) => {
      if (s.staged.length === 0 || s.status !== 'IN_PROGRESS') return s;
      const placed = [
        ...s.board.placed,
        ...s.staged.map((p) => ({
          row: p.row,
          col: p.col,
          letter: p.letter,
          is_blank: p.is_blank,
        })),
      ];
      const stagedIds = new Set(s.staged.map((p) => p.tile_id));
      const rack = s.rack.filter((t) => !stagedIds.has(t.tile_id));
      const bag = s.bag.slice();
      while (rack.length < 7 && bag.length > 0) rack.push(bag.shift()!);

      const cumulativeScore = s.cumulativeScore + score;
      const movesUsed = s.movesUsed + 1;
      const target = s.puzzle?.target_score ?? Number.POSITIVE_INFINITY;
      const moveCount = s.puzzle?.move_count ?? 0;
      // Play-on rule: the life only ends when the moves run out; it is then
      // graded against the target. Banking early goes through finishLocalLife.
      const ended = movesUsed >= moveCount;
      const won = ended && cumulativeScore >= target;
      const lost = ended && !won;

      return {
        board: { placed },
        rack,
        bag,
        staged: [],
        selectedTileId: null,
        cumulativeScore,
        movesUsed,
        moveHistory: [...s.moveHistory, { word, score, placements: s.staged }],
        status: won ? 'WON' : lost ? 'LOST' : 'IN_PROGRESS',
        dayWon: s.dayWon || won,
      };
    }),

  finishLocalLife: () =>
    set((s) => {
      if (s.status !== 'IN_PROGRESS') return s;
      const won = s.cumulativeScore >= (s.puzzle?.target_score ?? Number.POSITIVE_INFINITY);
      return { status: won ? 'WON' : 'LOST', staged: [], dayWon: s.dayWon || won };
    }),

  startNextLocalLife: () =>
    set((s) => {
      if (!s.puzzle || s.lifeNumber >= 3 || s.status === 'IN_PROGRESS') return s;
      return {
        previousLives: [
          ...s.previousLives,
          {
            life_number: s.lifeNumber,
            moves: s.moveHistory,
            status: s.status === 'WON' ? 'WON' : 'LOST',
            final_score: s.cumulativeScore,
          },
        ],
        board: s.puzzle.starting_board,
        rack: s.puzzle.daily_bag.slice(0, 7),
        bag: s.puzzle.daily_bag.slice(7),
        staged: [],
        selectedTileId: null,
        cumulativeScore: 0,
        movesUsed: 0,
        moveHistory: [],
        lifeNumber: s.lifeNumber + 1,
        status: 'IN_PROGRESS',
      };
    }),

  reset: () => set({ ...INITIAL }),
}));

/** Map a server LifeState response onto the store (authenticated play). */
export function snapshotFromServerLife(life: {
  life_id: string;
  life_number: number;
  status: LifeStatus;
  board_state: SerializedBoardDto;
  current_rack: TileDto[];
  current_score: number;
  moves_used: number;
  moves?: Array<{ primary_word: string; move_score: number }>;
}): Partial<GameSnapshot> {
  return {
    lifeId: life.life_id,
    lifeNumber: life.life_number,
    status: life.status,
    board: life.board_state,
    rack: life.current_rack,
    bag: [],
    cumulativeScore: life.current_score,
    movesUsed: life.moves_used,
    moveHistory: (life.moves ?? []).map((m) => ({
      word: m.primary_word,
      score: m.move_score,
      placements: [],
    })),
    localMode: false,
    dayWon: life.status === 'WON',
  };
}

/** Rack tiles still available to place (not currently staged). */
export function availableRack(s: Pick<GameSnapshot, 'rack' | 'staged'>): TileDto[] {
  const stagedIds = new Set(s.staged.map((p) => p.tile_id));
  return s.rack.filter((t) => !stagedIds.has(t.tile_id));
}

/**
 * Keep the player's hand arrangement across server reconciles: tiles that
 * survive stay in their previous order, fresh draws append in server order.
 */
export function preserveRackOrder(prev: TileDto[], next: TileDto[]): TileDto[] {
  const nextById = new Map(next.map((t) => [t.tile_id, t]));
  const kept = prev.filter((t) => nextById.has(t.tile_id)).map((t) => nextById.get(t.tile_id)!);
  const keptIds = new Set(kept.map((t) => t.tile_id));
  return [...kept, ...next.filter((t) => !keptIds.has(t.tile_id))];
}
